import { Descriptions as AntdDescriptions } from "antd";
import { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Descriptions from "../../general/antd/descriptions";
import PageDetails from "../../general/details";
import { useNavigate, useParams } from "react-router-dom";
import Spin from "../../general/antd/spin";
import AppContext from "../../../context/app/context";
import TrademarkContext from "../../../context/trademark/context";
import TrademarkContextProvider from "../../../context/trademark/provider";
import { ImCheckmark, ImCross } from "react-icons/im";
import Image from "../../general/antd/image";
import Tag from "../../general/antd/tag";
import AuthContext from "../../../context/auth/context";
import { Permissions } from "../../../utils/Permissions/Permissions";
import Button from "../../general/antd/button";
import ProductStatusModal from "../product-status-modal";

interface Props {}

const Trademark: React.FC<Props> = () => {
  const { t } = useTranslation();
  const { direction } = useContext(AppContext);
  const { details, actions, loading } = useContext(TrademarkContext);
  const getDetails = actions.getDetails;
  const { userPermissions } = useContext(AuthContext);
  const permissions = userPermissions?.length ? userPermissions : localStorage.getItem("permisionStoroge")?.split(",") ?? [];
  const canBulkChange = permissions.includes(Permissions.CAN_BULK_CHANGE_TRADEMARK_PRODUCT_STATUS);
  const [productStatusOpen, setProductStatusOpen] = useState(false);
  const { id } = useParams();
  const navigate = useNavigate();
  useEffect(() => {
    if (id) getDetails(Number(id));
  }, [direction, id, getDetails]);
  return (
    <>
      <PageDetails
        title={t("details")}
        subtitle={t("trademark")}
        buttonName={permissions.includes(Permissions.CAN_UPDATE_TRADEMARK) ? t("update_trademark") : undefined}
        loading={loading.includes('details')}
        onRefresh={() => actions.getDetails(Number(id))}
        onClick={() => navigate(`/trademarks/update/${id}`)}
        headerExtra={canBulkChange && (
          <Button onClick={() => setProductStatusOpen(true)} disabled={!details || details.id !== Number(id) || loading.includes("details")}>
            {t("trademark_product_status_title")}
          </Button>
        )}
      >
        {loading.includes("details") ? (
          <Spin />
        ) : (
          <Descriptions size="small" column={1} bordered>
            <AntdDescriptions.Item label={t("id")}>
              {details?.id}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("free_delivery")}>
                {details?.isOfferingFreeDelivery ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("accepted_exchange")}>
                {details?.acceptedExchange ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("export_purchase_invoice_report")}>
                {details?.exportPurchaseInvoiceReport ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
              <AntdDescriptions.Item label={t("has_offers")}>
                {details?.hasOffers ? (
                  <ImCheckmark color="green" />
                ) : (
                  <ImCross color="red" />
                )}
              </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("trademark_name")}>
              {details?.name ?? "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("min_order_value_for_free_delivery")}>
              {details?.minOrderValueForFreeDelivery ?? "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("max_order_value_for_free_delivery")}>
              {details?.maxOrderValueForFreeDelivery ?? "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("order")}>
              {details?.order ?? "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("is_new")}>
              {details?.isNew ? (
                  <ImCheckmark color="green" />
              ) : (
                  <ImCross color="red" />
              )}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("tags")}>
              {details?.tags?.length > 0
                  ? details?.tags?.map((tag) => {
                    return <Tag title={tag?.name} color={tag?.color} />;
                  })
                  : "-"}
            </AntdDescriptions.Item>
            {/* <AntdDescriptions.Itpem label={t("is_special")}>
              {details?.isSpecial ? (
                <ImCheckmark color="green" />
              ) : (
                <ImCross color="red" />
              )}
            </AntdDescriptions.Item> */}
            <AntdDescriptions.Item label={t("note")}>
              {details?.note ?? "-"}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("image")}>
              {details?.image ? (
                <Image
                  preview
                  height={80}
                  style={{
                    objectFit: "cover",
                  }}
                  width={80}
                  src={details?.image?.url}
                  alt={details?.image?.for}
                />
              ) : (
                <Tag color="red" title={t("not_found")} />
              )}
            </AntdDescriptions.Item>
            <AntdDescriptions.Item label={t("logo")}>
              {details?.logo ? (
                  <Image
                      preview
                      height={80}
                      style={{
                        objectFit: "cover",
                      }}
                      width={80}
                      src={details?.logo?.url}
                      alt={details?.logo?.for}
                  />
              ) : (
                  <Tag color="red" title={t("not_found")} />
              )}
            </AntdDescriptions.Item>
          </Descriptions>
        )}
      </PageDetails>
      {productStatusOpen && canBulkChange && details?.id === Number(id) && (
        <ProductStatusModal key={id} trademarkId={Number(id)} trademarkName={details.name} onClose={() => setProductStatusOpen(false)} />
      )}
    </>
  );
};

const TrademarkDetailsPage = () => {
  return (
    <TrademarkContextProvider>
      <Trademark />
    </TrademarkContextProvider>
  );
};
export default TrademarkDetailsPage;
